/**
 * Textos dos tours guiados e descrições breves das páginas de Estudo (/study-hub),
 * nos 10 idiomas nativos disponíveis em Ajustes. O idioma vem do i18n (idioma escolhido pelo usuário).
 *
 * target: seletor CSS do elemento destacado. Passos cujo elemento não está visível são pulados.
 */
export type TourLang = 'pt' | 'en' | 'es' | 'fr' | 'it' | 'de' | 'hu' | 'ja' | 'zh' | 'ru';
export type StudyTourKey = 'flashcards' | 'shadowing' | 'pronunciation' | 'writing' | 'listening' | 'grammar' | 'active_voice';
export type Pair = [string, string]; // [título, explicação]
export type Localized<T> = Record<TourLang, T>;

export interface TourStepDef { target: string; text: Localized<Pair> }
export interface StudyTourDef { description: Localized<string>; steps: TourStepDef[] }

export const TOUR_LANGS: TourLang[] = ['pt', 'en', 'es', 'fr', 'it', 'de', 'hu', 'ja', 'zh', 'ru'];

export const resolveTourLang = (lng?: string): TourLang => {
  const c = (lng || 'pt').toLowerCase().split('-')[0] as TourLang;
  return TOUR_LANGS.includes(c) ? c : 'en';
};

export const TOUR_UI: Localized<{ ask: string; askText: string; start: string; cancel: string; next: string; done: string; exit: string; help: string }> = {
  pt: { ask: 'Deseja iniciar um tour pela página?', askText: 'Vamos mostrar, passo a passo, cada função desta página.', start: 'Iniciar', cancel: 'Cancelar', next: 'Próximo', done: 'Concluir', exit: 'Sair do tour', help: 'Tour da página' },
  en: { ask: 'Would you like a tour of this page?', askText: "We'll show you every feature on this page, step by step.", start: 'Start', cancel: 'Cancel', next: 'Next', done: 'Finish', exit: 'Exit tour', help: 'Page tour' },
  es: { ask: '¿Quieres hacer un recorrido por la página?', askText: 'Te mostraremos, paso a paso, cada función de esta página.', start: 'Iniciar', cancel: 'Cancelar', next: 'Siguiente', done: 'Finalizar', exit: 'Salir del recorrido', help: 'Recorrido de la página' },
  fr: { ask: 'Voulez-vous faire une visite de la page ?', askText: 'Nous allons vous montrer, étape par étape, chaque fonction de cette page.', start: 'Commencer', cancel: 'Annuler', next: 'Suivant', done: 'Terminer', exit: 'Quitter la visite', help: 'Visite de la page' },
  it: { ask: 'Vuoi fare un tour della pagina?', askText: 'Ti mostreremo, passo dopo passo, ogni funzione di questa pagina.', start: 'Inizia', cancel: 'Annulla', next: 'Avanti', done: 'Fine', exit: 'Esci dal tour', help: 'Tour della pagina' },
  de: { ask: 'Möchtest du eine Tour durch diese Seite?', askText: 'Wir zeigen dir Schritt für Schritt jede Funktion dieser Seite.', start: 'Starten', cancel: 'Abbrechen', next: 'Weiter', done: 'Fertig', exit: 'Tour beenden', help: 'Seitentour' },
  hu: { ask: 'Szeretnél egy bemutatót az oldalról?', askText: 'Lépésről lépésre megmutatjuk az oldal minden funkcióját.', start: 'Indítás', cancel: 'Mégse', next: 'Tovább', done: 'Befejezés', exit: 'Kilépés', help: 'Oldal bemutató' },
  ja: { ask: 'このページのツアーを開始しますか？', askText: 'このページの各機能を順番にご紹介します。', start: '開始', cancel: 'キャンセル', next: '次へ', done: '完了', exit: 'ツアーを終了', help: 'ページツアー' },
  zh: { ask: '要开始页面导览吗？', askText: '我们将逐步为你介绍本页面的每个功能。', start: '开始', cancel: '取消', next: '下一步', done: '完成', exit: '退出导览', help: '页面导览' },
  ru: { ask: 'Начать тур по странице?', askText: 'Мы шаг за шагом покажем все функции этой страницы.', start: 'Начать', cancel: 'Отмена', next: 'Далее', done: 'Готово', exit: 'Выйти из тура', help: 'Тур по странице' },
};

// Passos comuns: abertura (título da página) e encerramento (botão ?)
export const TITLE = '[data-tour="page-title"]';
export const HELP = '[data-tour="tour-button"]';
export const replayStep: TourStepDef = {
  target: HELP,
  text: {
    pt: ['Reveja quando quiser', 'Toque no ? a qualquer momento para repetir este tour.'],
    en: ['Replay anytime', 'Tap ? at any time to replay this tour.'],
    es: ['Repítelo cuando quieras', 'Toca ? en cualquier momento para repetir este recorrido.'],
    fr: ['Revoyez-la quand vous voulez', 'Touchez ? à tout moment pour refaire cette visite.'],
    it: ['Rivedilo quando vuoi', 'Tocca ? in qualsiasi momento per ripetere il tour.'],
    de: ['Jederzeit wiederholen', 'Tippe jederzeit auf ?, um diese Tour zu wiederholen.'],
    hu: ['Bármikor megismételheted', 'Koppints a ? gombra a bemutató megismétléséhez.'],
    ja: ['いつでも見直せます', '「?」をタップすると、いつでもこのツアーを再生できます。'],
    zh: ['随时重温', '随时点击「?」即可重新查看本导览。'],
    ru: ['Повторите в любой момент', 'Нажмите ?, чтобы снова пройти этот тур.'],
  },
};

export const STUDY_TOURS: Record<StudyTourKey, StudyTourDef> = {
  flashcards: {
    description: {
      pt: 'Aprenda vocabulário através da repetição espaçada.',
      en: 'Learn vocabulary through spaced repetition.',
      es: 'Aprende vocabulario mediante la repetición espaciada.',
      fr: 'Apprenez du vocabulaire grâce à la répétition espacée.',
      it: 'Impara il vocabolario con la ripetizione dilazionata.',
      de: 'Lerne Vokabeln mit verteilter Wiederholung.',
      hu: 'Tanulj szókincset időközönkénti ismétléssel.',
      ja: '間隔反復で語彙を身につけましょう。',
      zh: '通过间隔重复学习词汇。',
      ru: 'Учите слова с помощью интервальных повторений.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Flashcards', 'Revise palavras no momento ideal: o app mostra cada cartão pouco antes de você esquecer.'],
        en: ['Flashcards', 'Review words at the right moment: each card shows up just before you would forget it.'],
        es: ['Flashcards', 'Repasa palabras en el momento justo: cada tarjeta aparece poco antes de que la olvides.'],
        fr: ['Flashcards', 'Révisez au bon moment : chaque carte réapparaît juste avant que vous ne l’oubliiez.'],
        it: ['Flashcard', 'Ripassa al momento giusto: ogni carta torna poco prima che tu la dimentichi.'],
        de: ['Karteikarten', 'Wiederhole im richtigen Moment: Jede Karte erscheint kurz bevor du sie vergisst.'],
        hu: ['Szókártyák', 'Ismételj a megfelelő pillanatban: minden kártya épp a felejtés előtt jelenik meg.'],
        ja: ['フラッシュカード', '忘れる直前に各カードが表示され、最適なタイミングで復習できます。'],
        zh: ['抽认卡', '在最佳时机复习：每张卡片会在你快忘记之前出现。'],
        ru: ['Карточки', 'Повторяйте вовремя: каждая карточка появляется прямо перед тем, как вы её забудете.'],
      } },
      { target: '[data-tour="flashcards-actions"]', text: {
        pt: ['Loja, importação e visualização', 'Busque baralhos prontos na loja, importe seus próprios cartões e mude o modo de visualização.'],
        en: ['Store, import and view', 'Get ready-made decks from the store, import your own cards and switch the view mode.'],
        es: ['Tienda, importar y vista', 'Consigue mazos en la tienda, importa tus tarjetas y cambia el modo de vista.'],
        fr: ['Boutique, import et affichage', 'Trouvez des paquets dans la boutique, importez vos cartes et changez l’affichage.'],
        it: ['Negozio, importa e vista', 'Trova mazzi nel negozio, importa le tue carte e cambia la visualizzazione.'],
        de: ['Shop, Import und Ansicht', 'Hol dir fertige Decks im Shop, importiere eigene Karten und wechsle die Ansicht.'],
        hu: ['Bolt, importálás, nézet', 'Szerezz kész paklikat, importáld a saját kártyáidat, és válts nézetet.'],
        ja: ['ストア・インポート・表示', 'ストアでデッキを入手し、自分のカードを取り込み、表示を切り替えられます。'],
        zh: ['商店、导入与视图', '在商店获取现成卡组、导入自己的卡片并切换显示方式。'],
        ru: ['Магазин, импорт и вид', 'Берите готовые колоды в магазине, импортируйте свои карточки и меняйте вид.'],
      } },
      { target: '[data-tour="flashcards-dashboard"]', text: {
        pt: ['Seu progresso', 'Veja sua média de cartões por dia, dias ativos, recorde e total de revisões.'],
        en: ['Your progress', 'See your daily card average, active days, record and total reviews.'],
        es: ['Tu progreso', 'Mira tu media diaria de tarjetas, días activos, récord y repasos.'],
        fr: ['Vos progrès', 'Voyez votre moyenne de cartes par jour, jours actifs, record et révisions.'],
        it: ['I tuoi progressi', 'Vedi la media giornaliera di carte, i giorni attivi, il record e i ripassi.'],
        de: ['Dein Fortschritt', 'Sieh deinen Tagesschnitt, aktive Tage, Rekord und Wiederholungen.'],
        hu: ['Haladásod', 'Nézd meg a napi átlagot, aktív napokat, rekordot és ismétléseket.'],
        ja: ['あなたの進捗', '1日の平均カード数、学習日数、最高記録、復習回数を確認できます。'],
        zh: ['你的进度', '查看每日平均卡片数、活跃天数、最高纪录和复习次数。'],
        ru: ['Ваш прогресс', 'Смотрите среднее число карточек в день, активные дни, рекорд и повторения.'],
      } },
      { target: '[data-tour="flashcards-decks"]', text: {
        pt: ['Seus baralhos', 'Escolha um baralho para começar a revisar. Cada um mostra os cartões prontos para hoje.'],
        en: ['Your decks', 'Pick a deck to start reviewing. Each one shows the cards due today.'],
        es: ['Tus mazos', 'Elige un mazo para repasar. Cada uno muestra las tarjetas de hoy.'],
        fr: ['Vos paquets', 'Choisissez un paquet pour réviser. Chacun indique les cartes du jour.'],
        it: ['I tuoi mazzi', 'Scegli un mazzo per ripassare. Ognuno mostra le carte di oggi.'],
        de: ['Deine Decks', 'Wähle ein Deck zum Wiederholen. Jedes zeigt die heute fälligen Karten.'],
        hu: ['Paklijaid', 'Válassz egy paklit az ismétléshez. Mindegyik mutatja a mai kártyákat.'],
        ja: ['あなたのデッキ', 'デッキを選んで復習を始めましょう。今日のカード数が表示されます。'],
        zh: ['你的卡组', '选择一个卡组开始复习，每个卡组都会显示今天待复习的卡片。'],
        ru: ['Ваши колоды', 'Выберите колоду для повторения. В каждой видно карточки на сегодня.'],
      } },
      replayStep,
    ],
  },

  shadowing: {
    description: {
      pt: 'Ouça e repita junto com o áudio para ganhar ritmo e entonação.',
      en: 'Listen and repeat along with the audio to build rhythm and intonation.',
      es: 'Escucha y repite junto al audio para ganar ritmo y entonación.',
      fr: 'Écoutez et répétez avec l’audio pour gagner en rythme et intonation.',
      it: 'Ascolta e ripeti insieme all’audio per migliorare ritmo e intonazione.',
      de: 'Hör zu und sprich mit dem Audio mit, um Rhythmus und Betonung zu üben.',
      hu: 'Hallgasd és ismételd a hanggal együtt a ritmusért és hanglejtésért.',
      ja: '音声に合わせて繰り返し、リズムとイントネーションを身につけましょう。',
      zh: '跟着音频一起听读，练习节奏与语调。',
      ru: 'Слушайте и повторяйте вместе с аудио, чтобы отработать ритм и интонацию.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Shadowing', 'Escolha um livro, ouça cada frase e repita ao mesmo tempo que o áudio.'],
        en: ['Shadowing', 'Pick a book, listen to each sentence and repeat it together with the audio.'],
        es: ['Shadowing', 'Elige un libro, escucha cada frase y repítela junto con el audio.'],
        fr: ['Shadowing', 'Choisissez un livre, écoutez chaque phrase et répétez-la avec l’audio.'],
        it: ['Shadowing', 'Scegli un libro, ascolta ogni frase e ripetila insieme all’audio.'],
        de: ['Shadowing', 'Wähle ein Buch, hör jeden Satz und sprich ihn zeitgleich mit.'],
        hu: ['Shadowing', 'Válassz könyvet, hallgasd meg a mondatokat, és ismételd a hanggal együtt.'],
        ja: ['シャドーイング', '本を選び、各文を聞きながら音声と同時に繰り返しましょう。'],
        zh: ['跟读', '选择一本书，听每个句子并与音频同步跟读。'],
        ru: ['Шэдоуинг', 'Выберите книгу, слушайте каждое предложение и повторяйте вместе с аудио.'],
      } },
      { target: '#tour-search-bar', text: {
        pt: ['Buscar livro', 'Procure um livro da sua estante pelo título ou autor.'],
        en: ['Find a book', 'Search your shelf by title or author.'],
        es: ['Buscar libro', 'Busca un libro de tu estantería por título o autor.'],
        fr: ['Chercher un livre', 'Recherchez un livre de votre étagère par titre ou auteur.'],
        it: ['Cerca un libro', 'Cerca un libro della tua libreria per titolo o autore.'],
        de: ['Buch suchen', 'Suche ein Buch in deinem Regal nach Titel oder Autor.'],
        hu: ['Könyv keresése', 'Keress a polcodon cím vagy szerző alapján.'],
        ja: ['本を探す', 'タイトルや著者で本棚の本を検索できます。'],
        zh: ['查找图书', '按书名或作者在书架中搜索。'],
        ru: ['Найти книгу', 'Ищите книгу на полке по названию или автору.'],
      } },
      { target: '#tour-library-btn', text: {
        pt: ['Mais livros', 'Abra a Biblioteca Online para adicionar novos livros gratuitos.'],
        en: ['More books', 'Open the Online Library to add new free books.'],
        es: ['Más libros', 'Abre la Biblioteca Online para añadir libros gratuitos.'],
        fr: ['Plus de livres', 'Ouvrez la Bibliothèque en ligne pour ajouter des livres gratuits.'],
        it: ['Altri libri', 'Apri la Biblioteca Online per aggiungere libri gratuiti.'],
        de: ['Mehr Bücher', 'Öffne die Online-Bibliothek, um kostenlose Bücher hinzuzufügen.'],
        hu: ['Több könyv', 'Nyisd meg az Online Könyvtárat ingyenes könyvekért.'],
        ja: ['本を追加', 'オンライン図書館を開いて無料の本を追加できます。'],
        zh: ['更多图书', '打开在线图书馆，添加免费图书。'],
        ru: ['Больше книг', 'Откройте онлайн-библиотеку, чтобы добавить бесплатные книги.'],
      } },
      replayStep,
    ],
  },

  pronunciation: {
    description: {
      pt: 'Ouça falantes nativos, grave sua voz e compare a pronúncia.',
      en: 'Listen to native speakers, record yourself and compare your pronunciation.',
      es: 'Escucha a nativos, graba tu voz y compara la pronunciación.',
      fr: 'Écoutez des natifs, enregistrez-vous et comparez votre prononciation.',
      it: 'Ascolta i madrelingua, registra la tua voce e confronta la pronuncia.',
      de: 'Hör Muttersprachlern zu, nimm dich auf und vergleiche die Aussprache.',
      hu: 'Hallgass anyanyelvieket, vedd fel magad, és hasonlítsd össze a kiejtést.',
      ja: 'ネイティブの音声を聞き、自分の声を録音して発音を比べましょう。',
      zh: '聆听母语者发音，录下自己的声音并进行对比。',
      ru: 'Слушайте носителей, записывайте себя и сравнивайте произношение.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Pronúncia', 'Treine a fala: ouça a frase original, grave a sua e veja onde melhorar.'],
        en: ['Pronunciation', 'Train your speech: hear the original, record yours and see what to improve.'],
        es: ['Pronunciación', 'Entrena tu habla: escucha la frase, graba la tuya y mira qué mejorar.'],
        fr: ['Prononciation', 'Entraînez-vous : écoutez l’original, enregistrez-vous et voyez quoi améliorer.'],
        it: ['Pronuncia', 'Allena la voce: ascolta l’originale, registrati e scopri cosa migliorare.'],
        de: ['Aussprache', 'Trainiere: hör das Original, nimm dich auf und sieh, was besser geht.'],
        hu: ['Kiejtés', 'Gyakorolj: hallgasd meg az eredetit, vedd fel a sajátod, és javíts.'],
        ja: ['発音', '原音を聞き、自分の声を録音して、改善点を確認しましょう。'],
        zh: ['发音', '听原声、录下自己的发音，看看哪里需要改进。'],
        ru: ['Произношение', 'Слушайте оригинал, записывайте себя и смотрите, что улучшить.'],
      } },
      { target: '[data-tour="pron-selectors"]', text: {
        pt: ['Idioma e nível', 'Escolha o idioma e a dificuldade das frases que você vai treinar.'],
        en: ['Language and level', 'Choose the language and difficulty of the sentences.'],
        es: ['Idioma y nivel', 'Elige el idioma y la dificultad de las frases.'],
        fr: ['Langue et niveau', 'Choisissez la langue et la difficulté des phrases.'],
        it: ['Lingua e livello', 'Scegli la lingua e la difficoltà delle frasi.'],
        de: ['Sprache und Niveau', 'Wähle Sprache und Schwierigkeit der Sätze.'],
        hu: ['Nyelv és szint', 'Válaszd ki a nyelvet és a mondatok nehézségét.'],
        ja: ['言語とレベル', '練習する文の言語と難易度を選びます。'],
        zh: ['语言与级别', '选择练习句子的语言和难度。'],
        ru: ['Язык и уровень', 'Выберите язык и сложность фраз.'],
      } },
      { target: '[data-tour="pron-mode"]', text: {
        pt: ['Modo de estudo', 'Geral traz frases novas; focado repete as frases em que você teve dificuldade.'],
        en: ['Study mode', 'General brings new sentences; focused repeats the ones you struggled with.'],
        es: ['Modo de estudio', 'General trae frases nuevas; enfocado repite las que te costaron.'],
        fr: ['Mode d’étude', 'Général propose des phrases nouvelles ; ciblé reprend vos difficultés.'],
        it: ['Modalità di studio', 'Generale porta frasi nuove; mirato ripete quelle difficili.'],
        de: ['Lernmodus', 'Allgemein bringt neue Sätze; fokussiert wiederholt schwierige.'],
        hu: ['Tanulási mód', 'Általános: új mondatok; fókuszált: a nehezebbek ismétlése.'],
        ja: ['学習モード', '一般は新しい文、集中は苦手だった文を繰り返します。'],
        zh: ['学习模式', '常规模式提供新句子；专注模式重复你出错的句子。'],
        ru: ['Режим', 'Общий даёт новые фразы; фокус повторяет трудные.'],
      } },
      { target: '[data-tour="pron-card"]', text: {
        pt: ['A frase', 'Leia a frase do cartão e acompanhe quantas faltam no contador.'],
        en: ['The sentence', 'Read the sentence on the card and track how many are left.'],
        es: ['La frase', 'Lee la frase de la tarjeta y mira cuántas faltan.'],
        fr: ['La phrase', 'Lisez la phrase de la carte et suivez le compteur.'],
        it: ['La frase', 'Leggi la frase della carta e segui il contatore.'],
        de: ['Der Satz', 'Lies den Satz auf der Karte und behalte den Zähler im Blick.'],
        hu: ['A mondat', 'Olvasd el a kártya mondatát, és kövesd a számlálót.'],
        ja: ['文', 'カードの文を読み、残りの数をカウンターで確認します。'],
        zh: ['句子', '阅读卡片上的句子，并查看剩余数量。'],
        ru: ['Фраза', 'Прочитайте фразу на карточке и следите за счётчиком.'],
      } },
      { target: '[data-tour="pron-native"]', text: {
        pt: ['Áudio nativo', 'Ouça a pronúncia original quantas vezes quiser.'],
        en: ['Native audio', 'Listen to the original pronunciation as many times as you like.'],
        es: ['Audio nativo', 'Escucha la pronunciación original cuantas veces quieras.'],
        fr: ['Audio natif', 'Écoutez la prononciation originale autant de fois que vous voulez.'],
        it: ['Audio madrelingua', 'Ascolta la pronuncia originale tutte le volte che vuoi.'],
        de: ['Original-Audio', 'Hör dir die Originalaussprache so oft du willst an.'],
        hu: ['Eredeti hang', 'Hallgasd meg az eredeti kiejtést, ahányszor csak akarod.'],
        ja: ['ネイティブ音声', '原音の発音を何度でも聞けます。'],
        zh: ['原声音频', '想听几次原声发音都可以。'],
        ru: ['Оригинал', 'Слушайте оригинальное произношение сколько угодно.'],
      } },
      { target: '[data-tour="pron-record"]', text: {
        pt: ['Grave sua voz', 'Toque para gravar e receba a análise da sua pronúncia.'],
        en: ['Record yourself', 'Tap to record and get an analysis of your pronunciation.'],
        es: ['Graba tu voz', 'Toca para grabar y recibe el análisis de tu pronunciación.'],
        fr: ['Enregistrez-vous', 'Touchez pour enregistrer et recevoir l’analyse de votre prononciation.'],
        it: ['Registrati', 'Tocca per registrare e ricevere l’analisi della pronuncia.'],
        de: ['Nimm dich auf', 'Tippe zum Aufnehmen und erhalte eine Analyse deiner Aussprache.'],
        hu: ['Vedd fel magad', 'Koppints a felvételhez, és kapj elemzést a kiejtésedről.'],
        ja: ['録音する', 'タップして録音すると、発音の分析が表示されます。'],
        zh: ['录制声音', '点击录音，即可获得发音分析。'],
        ru: ['Запишите себя', 'Нажмите для записи и получите разбор произношения.'],
      } },
      replayStep,
    ],
  },

  writing: {
    description: {
      pt: 'Escreva e traduza textos com correção do Professor LibriTui.',
      en: 'Write and translate texts with feedback from Professor LibriTui.',
      es: 'Escribe y traduce textos con la corrección del Profesor LibriTui.',
      fr: 'Écrivez et traduisez avec la correction du Professeur LibriTui.',
      it: 'Scrivi e traduci testi con la correzione del Professor LibriTui.',
      de: 'Schreibe und übersetze Texte mit Korrektur von Professor LibriTui.',
      hu: 'Írj és fordíts szövegeket LibriTui professzor javításával.',
      ja: 'LibriTui先生の添削で、書く力と翻訳力を伸ばしましょう。',
      zh: '在 LibriTui 老师的批改下练习写作与翻译。',
      ru: 'Пишите и переводите тексты с проверкой профессора LibriTui.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Escrita', 'Pratique escrevendo: o Professor LibriTui corrige e explica cada ajuste.'],
        en: ['Writing', 'Practice by writing: Professor LibriTui corrects and explains every change.'],
        es: ['Escritura', 'Practica escribiendo: el Profesor LibriTui corrige y explica cada cambio.'],
        fr: ['Écriture', 'Entraînez-vous : le Professeur LibriTui corrige et explique chaque modification.'],
        it: ['Scrittura', 'Esercitati: il Professor LibriTui corregge e spiega ogni modifica.'],
        de: ['Schreiben', 'Übe schreibend: Professor LibriTui korrigiert und erklärt jede Änderung.'],
        hu: ['Írás', 'Gyakorolj írással: LibriTui professzor javít és elmagyarázza a változásokat.'],
        ja: ['ライティング', '書いて練習しましょう。LibriTui先生が添削し、理由を説明します。'],
        zh: ['写作', '动笔练习：LibriTui 老师会批改并解释每处修改。'],
        ru: ['Письмо', 'Пишите: профессор LibriTui исправит и объяснит каждую правку.'],
      } },
      { target: '[data-tour="writing-language"]', text: {
        pt: ['Idioma', 'Escolha o idioma em que você quer escrever.'],
        en: ['Language', 'Choose the language you want to write in.'],
        es: ['Idioma', 'Elige el idioma en el que quieres escribir.'],
        fr: ['Langue', 'Choisissez la langue dans laquelle écrire.'],
        it: ['Lingua', 'Scegli la lingua in cui vuoi scrivere.'],
        de: ['Sprache', 'Wähle die Sprache, in der du schreiben willst.'],
        hu: ['Nyelv', 'Válaszd ki, milyen nyelven szeretnél írni.'],
        ja: ['言語', '書きたい言語を選びます。'],
        zh: ['语言', '选择你想用来写作的语言。'],
        ru: ['Язык', 'Выберите язык, на котором хотите писать.'],
      } },
      { target: '[data-tour="writing-level"]', text: {
        pt: ['Nível', 'Ajuste a dificuldade dos textos ao seu momento.'],
        en: ['Level', 'Match the text difficulty to your level.'],
        es: ['Nivel', 'Ajusta la dificultad de los textos a tu nivel.'],
        fr: ['Niveau', 'Adaptez la difficulté des textes à votre niveau.'],
        it: ['Livello', 'Adatta la difficoltà dei testi al tuo livello.'],
        de: ['Niveau', 'Passe die Schwierigkeit der Texte an dein Niveau an.'],
        hu: ['Szint', 'Igazítsd a szövegek nehézségét a szintedhez.'],
        ja: ['レベル', '自分に合った難易度に調整します。'],
        zh: ['级别', '根据你的水平调整文本难度。'],
        ru: ['Уровень', 'Подберите сложность текстов под свой уровень.'],
      } },
      { target: '[data-tour="writing-mode"]', text: {
        pt: ['Tipo de prática', 'Tradução: você traduz histórias. Ativo: você escreve livremente.'],
        en: ['Practice type', 'Translation: translate stories. Active: write freely.'],
        es: ['Tipo de práctica', 'Traducción: traduces historias. Activo: escribes libremente.'],
        fr: ['Type de pratique', 'Traduction : traduisez des histoires. Actif : écrivez librement.'],
        it: ['Tipo di pratica', 'Traduzione: traduci storie. Attivo: scrivi liberamente.'],
        de: ['Übungsart', 'Übersetzung: Geschichten übersetzen. Aktiv: frei schreiben.'],
        hu: ['Gyakorlat típusa', 'Fordítás: történeteket fordítasz. Aktív: szabadon írsz.'],
        ja: ['練習タイプ', '翻訳：物語を訳します。アクティブ：自由に書きます。'],
        zh: ['练习类型', '翻译：翻译故事。主动：自由写作。'],
        ru: ['Тип практики', 'Перевод: переводите истории. Активный: пишите свободно.'],
      } },
      { target: '[data-tour="writing-focus"]', text: {
        pt: ['Foco', 'Geral para conteúdo novo; focado para revisar seus erros.'],
        en: ['Focus', 'General for new content; focused to review your mistakes.'],
        es: ['Enfoque', 'General para contenido nuevo; enfocado para repasar errores.'],
        fr: ['Focus', 'Général pour du nouveau ; ciblé pour revoir vos erreurs.'],
        it: ['Focus', 'Generale per contenuti nuovi; mirato per ripassare gli errori.'],
        de: ['Fokus', 'Allgemein für Neues; fokussiert, um Fehler zu wiederholen.'],
        hu: ['Fókusz', 'Általános: új tartalom; fókuszált: hibák ismétlése.'],
        ja: ['フォーカス', '一般は新しい内容、集中は間違いの復習です。'],
        zh: ['重点', '常规学习新内容；专注模式复习错误。'],
        ru: ['Фокус', 'Общий для нового; фокус для повторения ошибок.'],
      } },
      { target: '#tour-writing-input', text: {
        pt: ['Sua resposta', 'Escreva aqui e envie para receber a correção detalhada.'],
        en: ['Your answer', 'Write here and send it to get detailed feedback.'],
        es: ['Tu respuesta', 'Escribe aquí y envía para recibir la corrección.'],
        fr: ['Votre réponse', 'Écrivez ici et envoyez pour recevoir la correction.'],
        it: ['La tua risposta', 'Scrivi qui e invia per ricevere la correzione.'],
        de: ['Deine Antwort', 'Schreib hier und sende sie für eine ausführliche Korrektur.'],
        hu: ['Válaszod', 'Írd ide, és küldd el a részletes javításért.'],
        ja: ['あなたの回答', 'ここに書いて送信すると、詳しい添削が届きます。'],
        zh: ['你的答案', '在此书写并提交，即可获得详细批改。'],
        ru: ['Ваш ответ', 'Пишите здесь и отправляйте, чтобы получить подробный разбор.'],
      } },
      replayStep,
    ],
  },

  listening: {
    description: {
      pt: 'Ouça palavras e frases e treine sua compreensão auditiva.',
      en: 'Listen to words and sentences and train your listening comprehension.',
      es: 'Escucha palabras y frases y entrena tu comprensión auditiva.',
      fr: 'Écoutez des mots et des phrases pour entraîner votre compréhension orale.',
      it: 'Ascolta parole e frasi e allena la comprensione orale.',
      de: 'Hör Wörter und Sätze und trainiere dein Hörverständnis.',
      hu: 'Hallgass szavakat és mondatokat, fejleszd a hallás utáni értést.',
      ja: '単語や文を聞いて、リスニング力を鍛えましょう。',
      zh: '聆听单词和句子，训练听力理解。',
      ru: 'Слушайте слова и фразы и тренируйте понимание на слух.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Listening', 'Ouça o áudio e mostre que entendeu: digite ou monte a frase.'],
        en: ['Listening', 'Listen to the audio and show you understood: type or build the sentence.'],
        es: ['Listening', 'Escucha el audio y demuestra que entendiste: escribe o arma la frase.'],
        fr: ['Écoute', 'Écoutez l’audio et prouvez que vous avez compris : tapez ou reconstituez la phrase.'],
        it: ['Ascolto', 'Ascolta l’audio e dimostra di aver capito: scrivi o componi la frase.'],
        de: ['Hören', 'Hör das Audio und zeig, dass du es verstehst: tippe oder baue den Satz.'],
        hu: ['Hallás', 'Hallgasd meg, és mutasd meg, hogy érted: írd be vagy rakd össze a mondatot.'],
        ja: ['リスニング', '音声を聞いて、文を入力するか並べ替えて答えます。'],
        zh: ['听力', '听音频并作答：输入或拼出句子。'],
        ru: ['Аудирование', 'Слушайте и отвечайте: введите фразу или соберите её из блоков.'],
      } },
      { target: '[data-tour="listening-language"]', text: {
        pt: ['Idioma', 'Escolha o idioma dos áudios.'], en: ['Language', 'Choose the audio language.'], es: ['Idioma', 'Elige el idioma de los audios.'], fr: ['Langue', 'Choisissez la langue des audios.'], it: ['Lingua', 'Scegli la lingua degli audio.'], de: ['Sprache', 'Wähle die Sprache der Audios.'], hu: ['Nyelv', 'Válaszd ki a hanganyag nyelvét.'], ja: ['言語', '音声の言語を選びます。'], zh: ['语言', '选择音频语言。'], ru: ['Язык', 'Выберите язык аудио.'],
      } },
      { target: '[data-tour="listening-level"]', text: {
        pt: ['Nível', 'Defina a dificuldade dos áudios.'], en: ['Level', 'Set the audio difficulty.'], es: ['Nivel', 'Define la dificultad de los audios.'], fr: ['Niveau', 'Réglez la difficulté des audios.'], it: ['Livello', 'Imposta la difficoltà degli audio.'], de: ['Niveau', 'Lege die Schwierigkeit der Audios fest.'], hu: ['Szint', 'Állítsd be a hanganyag nehézségét.'], ja: ['レベル', '音声の難易度を設定します。'], zh: ['级别', '设置音频难度。'], ru: ['Уровень', 'Задайте сложность аудио.'],
      } },
      { target: '[data-tour="listening-content"]', text: {
        pt: ['Palavras ou frases', 'Treine palavras soltas ou frases completas.'], en: ['Words or sentences', 'Train single words or full sentences.'], es: ['Palabras o frases', 'Entrena palabras sueltas o frases completas.'], fr: ['Mots ou phrases', 'Entraînez des mots isolés ou des phrases entières.'], it: ['Parole o frasi', 'Allena parole singole o frasi intere.'], de: ['Wörter oder Sätze', 'Übe einzelne Wörter oder ganze Sätze.'], hu: ['Szavak vagy mondatok', 'Gyakorolj szavakat vagy teljes mondatokat.'], ja: ['単語か文か', '単語か文全体かを選んで練習します。'], zh: ['单词或句子', '练习单个单词或完整句子。'], ru: ['Слова или фразы', 'Тренируйте отдельные слова или целые фразы.'],
      } },
      { target: '[data-tour="listening-play"]', text: {
        pt: ['Ouvir', 'Toque para tocar o áudio; toque de novo para parar.'], en: ['Play', 'Tap to play the audio; tap again to stop.'], es: ['Escuchar', 'Toca para reproducir; toca de nuevo para parar.'], fr: ['Écouter', 'Touchez pour lire l’audio, encore pour arrêter.'], it: ['Ascolta', 'Tocca per riprodurre; tocca di nuovo per fermare.'], de: ['Abspielen', 'Tippe zum Abspielen, erneut zum Stoppen.'], hu: ['Lejátszás', 'Koppints a lejátszáshoz, újra a leállításhoz.'], ja: ['再生', 'タップで再生、もう一度タップで停止します。'], zh: ['播放', '点击播放音频，再次点击停止。'], ru: ['Слушать', 'Нажмите, чтобы включить; ещё раз — чтобы остановить.'],
      } },
      { target: '[data-tour="listening-speed"]', text: {
        pt: ['Velocidade', 'Deixe o áudio mais lento quando precisar.'], en: ['Speed', 'Slow the audio down when you need to.'], es: ['Velocidad', 'Haz el audio más lento cuando lo necesites.'], fr: ['Vitesse', 'Ralentissez l’audio si besoin.'], it: ['Velocità', 'Rallenta l’audio quando serve.'], de: ['Tempo', 'Verlangsame das Audio bei Bedarf.'], hu: ['Sebesség', 'Lassítsd le a hangot, ha kell.'], ja: ['速度', '必要に応じて音声をゆっくりにできます。'], zh: ['速度', '需要时可放慢音频。'], ru: ['Скорость', 'Замедляйте аудио, когда нужно.'],
      } },
      { target: '[data-tour="listening-hint"]', text: {
        pt: ['Dica', 'Mostra uma pista quando você travar.'], en: ['Hint', 'Shows a clue when you get stuck.'], es: ['Pista', 'Muestra una pista cuando te atasques.'], fr: ['Indice', 'Affiche un indice si vous bloquez.'], it: ['Suggerimento', 'Mostra un indizio quando ti blocchi.'], de: ['Hinweis', 'Zeigt einen Tipp, wenn du feststeckst.'], hu: ['Tipp', 'Segítséget ad, ha elakadsz.'], ja: ['ヒント', '詰まったときにヒントを表示します。'], zh: ['提示', '卡住时显示提示。'], ru: ['Подсказка', 'Показывает подсказку, если вы застряли.'],
      } },
      { target: '[data-tour="listening-mode"]', text: {
        pt: ['Responder', 'Digite o que ouviu ou organize os blocos de palavras.'], en: ['Answer', 'Type what you heard or arrange the word blocks.'], es: ['Responder', 'Escribe lo que oíste u ordena los bloques.'], fr: ['Répondre', 'Tapez ce que vous avez entendu ou ordonnez les blocs.'], it: ['Rispondi', 'Scrivi ciò che hai sentito o ordina i blocchi.'], de: ['Antworten', 'Tippe, was du gehört hast, oder ordne die Blöcke.'], hu: ['Válasz', 'Írd be, amit hallottál, vagy rendezd a blokkokat.'], ja: ['回答', '聞いた内容を入力するか、単語ブロックを並べます。'], zh: ['作答', '输入你听到的内容或排列单词块。'], ru: ['Ответ', 'Введите услышанное или расставьте блоки слов.'],
      } },
      replayStep,
    ],
  },

  grammar: {
    description: {
      pt: 'Entenda a estrutura do idioma com lições curtas e quizzes.',
      en: 'Understand the language structure with short lessons and quizzes.',
      es: 'Entiende la estructura del idioma con lecciones cortas y quizzes.',
      fr: 'Comprenez la structure de la langue avec des leçons courtes et des quiz.',
      it: 'Capisci la struttura della lingua con lezioni brevi e quiz.',
      de: 'Verstehe die Sprachstruktur mit kurzen Lektionen und Quiz.',
      hu: 'Értsd meg a nyelv szerkezetét rövid leckékkel és kvízekkel.',
      ja: '短いレッスンとクイズで言語の仕組みを理解しましょう。',
      zh: '通过简短课程和测验理解语言结构。',
      ru: 'Разбирайтесь в структуре языка с короткими уроками и тестами.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Gramática', 'Cada tópico tem uma explicação curta e um quiz para fixar.'],
        en: ['Grammar', 'Each topic has a short explanation and a quiz to lock it in.'],
        es: ['Gramática', 'Cada tema tiene una explicación corta y un quiz para fijarlo.'],
        fr: ['Grammaire', 'Chaque sujet a une courte explication et un quiz pour l’ancrer.'],
        it: ['Grammatica', 'Ogni argomento ha una breve spiegazione e un quiz.'],
        de: ['Grammatik', 'Jedes Thema hat eine kurze Erklärung und ein Quiz.'],
        hu: ['Nyelvtan', 'Minden témához rövid magyarázat és kvíz tartozik.'],
        ja: ['文法', '各トピックに短い解説と定着のためのクイズがあります。'],
        zh: ['语法', '每个主题都有简短讲解和巩固测验。'],
        ru: ['Грамматика', 'В каждой теме есть короткое объяснение и тест.'],
      } },
      { target: '[data-tour="grammar-level"]', text: {
        pt: ['Nível', 'Escolha o nível (A1 a C2) dos tópicos.'], en: ['Level', 'Choose the topic level (A1 to C2).'], es: ['Nivel', 'Elige el nivel (A1 a C2) de los temas.'], fr: ['Niveau', 'Choisissez le niveau (A1 à C2) des sujets.'], it: ['Livello', 'Scegli il livello (A1–C2) degli argomenti.'], de: ['Niveau', 'Wähle das Niveau (A1 bis C2) der Themen.'], hu: ['Szint', 'Válaszd ki a témák szintjét (A1–C2).'], ja: ['レベル', 'トピックのレベル（A1〜C2）を選びます。'], zh: ['级别', '选择主题级别（A1 至 C2）。'], ru: ['Уровень', 'Выберите уровень тем (A1–C2).'],
      } },
      { target: '[data-tour="grammar-lang"]', text: {
        pt: ['Idioma', 'Troque o idioma que você está estudando.'], en: ['Language', 'Switch the language you are studying.'], es: ['Idioma', 'Cambia el idioma que estudias.'], fr: ['Langue', 'Changez la langue étudiée.'], it: ['Lingua', 'Cambia la lingua che stai studiando.'], de: ['Sprache', 'Wechsle die Lernsprache.'], hu: ['Nyelv', 'Válts a tanult nyelvek között.'], ja: ['言語', '学習中の言語を切り替えます。'], zh: ['语言', '切换正在学习的语言。'], ru: ['Язык', 'Смените изучаемый язык.'],
      } },
      { target: '[data-tour="grammar-roadmap"]', text: {
        pt: ['Trilha de tópicos', 'Avance em ordem: concluir um tópico desbloqueia o próximo.'], en: ['Topic path', 'Move in order: finishing a topic unlocks the next one.'], es: ['Ruta de temas', 'Avanza en orden: terminar un tema desbloquea el siguiente.'], fr: ['Parcours', 'Avancez dans l’ordre : finir un sujet débloque le suivant.'], it: ['Percorso', 'Procedi in ordine: completare un argomento sblocca il successivo.'], de: ['Themenpfad', 'Geh der Reihe nach vor: Ein Thema schaltet das nächste frei.'], hu: ['Témaút', 'Haladj sorban: egy téma befejezése feloldja a következőt.'], ja: ['トピックの道', '順番に進みます。完了すると次のトピックが解放されます。'], zh: ['主题路线', '按顺序前进：完成一个主题即可解锁下一个。'], ru: ['Путь тем', 'Идите по порядку: завершённая тема открывает следующую.'],
      } },
      replayStep,
    ],
  },

  active_voice: {
    description: {
      pt: 'Converse por voz com a IA e pratique uma fala natural.',
      en: 'Talk with the AI by voice and practice natural speaking.',
      es: 'Conversa por voz con la IA y practica un habla natural.',
      fr: 'Parlez à voix haute avec l’IA et pratiquez une expression naturelle.',
      it: 'Parla a voce con l’IA e allena una conversazione naturale.',
      de: 'Sprich mit der KI und übe natürliches Sprechen.',
      hu: 'Beszélgess hangon az MI-vel, és gyakorold a természetes beszédet.',
      ja: 'AIと音声で会話し、自然な話し方を練習しましょう。',
      zh: '与 AI 语音对话，练习自然表达。',
      ru: 'Говорите с ИИ голосом и тренируйте естественную речь.',
    },
    steps: [
      { target: TITLE, text: {
        pt: ['Voz Ativa', 'Uma conversa de verdade: você fala, a IA responde e ajuda você a se corrigir.'],
        en: ['Active Voice', 'A real conversation: you speak, the AI replies and helps you improve.'],
        es: ['Voz Activa', 'Una conversación real: hablas, la IA responde y te ayuda a mejorar.'],
        fr: ['Voix Active', 'Une vraie conversation : vous parlez, l’IA répond et vous aide.'],
        it: ['Voce Attiva', 'Una vera conversazione: parli, l’IA risponde e ti aiuta a migliorare.'],
        de: ['Aktive Stimme', 'Ein echtes Gespräch: Du sprichst, die KI antwortet und hilft dir.'],
        hu: ['Aktív hang', 'Valódi beszélgetés: te beszélsz, az MI válaszol és segít fejlődni.'],
        ja: ['アクティブボイス', '本当の会話です。あなたが話し、AIが答えて改善を手伝います。'],
        zh: ['主动口语', '真实对话：你来说，AI 回应并帮你改进。'],
        ru: ['Активная речь', 'Настоящий разговор: вы говорите, ИИ отвечает и помогает улучшить речь.'],
      } },
      { target: '[data-tour="av-new"]', text: {
        pt: ['Nova conversa', 'Comece uma conversa nova no idioma que você está estudando.'], en: ['New conversation', 'Start a new conversation in the language you are studying.'], es: ['Nueva conversación', 'Empieza una conversación en el idioma que estudias.'], fr: ['Nouvelle conversation', 'Lancez une conversation dans la langue étudiée.'], it: ['Nuova conversazione', 'Avvia una conversazione nella lingua che studi.'], de: ['Neues Gespräch', 'Starte ein Gespräch in deiner Lernsprache.'], hu: ['Új beszélgetés', 'Kezdj új beszélgetést a tanult nyelven.'], ja: ['新しい会話', '学習中の言語で新しい会話を始めます。'], zh: ['新对话', '用正在学习的语言开始新对话。'], ru: ['Новый разговор', 'Начните разговор на изучаемом языке.'],
      } },
      { target: '[data-tour="av-rooms"]', text: {
        pt: ['Suas salas', 'Volte às conversas anteriores, organizadas por idioma.'], en: ['Your rooms', 'Return to previous conversations, organized by language.'], es: ['Tus salas', 'Vuelve a conversaciones anteriores, por idioma.'], fr: ['Vos salons', 'Retrouvez vos conversations, classées par langue.'], it: ['Le tue stanze', 'Riprendi le conversazioni, organizzate per lingua.'], de: ['Deine Räume', 'Kehre zu früheren Gesprächen zurück, nach Sprache sortiert.'], hu: ['Szobáid', 'Térj vissza a korábbi, nyelv szerint rendezett beszélgetésekhez.'], ja: ['あなたのルーム', '言語別に整理された過去の会話に戻れます。'], zh: ['你的房间', '回到按语言整理的历史对话。'], ru: ['Ваши комнаты', 'Возвращайтесь к прошлым разговорам, разложенным по языкам.'],
      } },
      replayStep,
    ],
  },
};
